import { jsPDF } from 'jspdf';
import { getCategoriaCorHex } from 'utils/iniciativa-categoria';

export interface VotacaoEtapa1ExportItem {
  titulo: string;
  descricao: string;
  resultado: string;
  videoUrl?: string;
  tema?: string;
  categoryPremio?: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

const CSV_HEADERS = ['Título', 'Descrição', 'Resultado', 'Link do vídeo'];

function sanitizeFilename(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function escapeCsvValue(value: string): string {
  const normalized = value.replace(/\r?\n|\r/g, ' ').trim();
  const escaped = normalized.replace(/"/g, '""');
  return `"${escaped}"`;
}

function isHttpUrl(value?: string): boolean {
  if (!value) return false;
  return /^https?:\/\//i.test(value.trim());
}

function buildCsvVideoCell(videoUrl?: string): string {
  if (!isHttpUrl(videoUrl)) {
    return escapeCsvValue(videoUrl || '');
  }

  return escapeCsvValue(String(videoUrl || '').trim());
}

function truncateTextForWidth(doc: jsPDF, text: string, maxWidth: number): string {
  const normalized = (text || '-').replace(/\s+/g, ' ').trim() || '-';
  if (doc.getTextWidth(normalized) <= maxWidth) {
    return normalized;
  }

  const ellipsis = '...';
  let truncated = normalized;

  while (truncated.length > 0 && doc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }

  return `${truncated}${ellipsis}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function exportarIniciativasCsv(
  iniciativas: VotacaoEtapa1ExportItem[],
  baseFilename = 'iniciativas-selecionadas'
): void {
  const rows = iniciativas.map((item) => {
    return [
      escapeCsvValue(item.titulo),
      escapeCsvValue(item.descricao),
      escapeCsvValue(item.resultado),
      buildCsvVideoCell(item.videoUrl)
    ].join(';');
  });

  const csvContent = [CSV_HEADERS.join(';'), ...rows].join('\n');
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const filename = `${sanitizeFilename(baseFilename) || 'iniciativas-selecionadas'}.csv`;

  triggerDownload(blob, filename);
}

export function exportarIniciativasPdf(
  iniciativas: VotacaoEtapa1ExportItem[],
  baseFilename = 'iniciativas-selecionadas',
  titulo = 'Exportação de iniciativas selecionadas'
): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const contentWidth = pageWidth - marginX * 2;
  const cardPadding = 14;
  const sectionGap = 10;
  const lineHeight = 13;

  let cursorY = 42;

  const ensurePageSpace = (requiredHeight: number) => {
    if (cursorY + requiredHeight > pageHeight - 36) {
      doc.addPage();
      cursorY = 42;
    }
  };

  const split = (text: string, width: number) => {
    const normalized = (text || '-').replace(/\s+/g, ' ').trim();
    return doc.splitTextToSize(normalized, width) as string[];
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(titulo, marginX, cursorY);
  cursorY += 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, marginX, cursorY);
  cursorY += 18;

  iniciativas.forEach((item, index) => {
    const cardInnerWidth = contentWidth - cardPadding * 2;

    const titleLines = split(item.titulo, cardInnerWidth);
    const descricaoLines = split(item.descricao, cardInnerWidth);
    const resultadoLines = split(item.resultado, cardInnerWidth);
    const hasVideoUrl = isHttpUrl(item.videoUrl);
    const videoText = hasVideoUrl
      ? truncateTextForWidth(doc, item.videoUrl || '', cardInnerWidth)
      : '-';

    const hasTema = !!(item.tema && item.tema.trim());
    const temaTagHeight = 18;
    const temaTagGap = 6;

    const titleHeight = titleLines.length * 15;
    const sectionLabelHeight = 12;
    const descricaoHeight = descricaoLines.length * lineHeight;
    const resultadoHeight = resultadoLines.length * lineHeight;
    const videoHeight = lineHeight;

    const cardHeight =
      cardPadding +
      18 +
      (hasTema ? temaTagHeight + temaTagGap : 0) +
      titleHeight +
      sectionGap +
      sectionLabelHeight +
      descricaoHeight +
      sectionGap +
      sectionLabelHeight +
      resultadoHeight +
      sectionGap +
      sectionLabelHeight +
      videoHeight +
      cardPadding;

    ensurePageSpace(cardHeight + 12);

    const cardX = marginX;
    const cardY = cursorY;
    const headerHeight = 24 + (hasTema ? temaTagHeight + temaTagGap : 0);

    doc.setDrawColor(51, 84, 253);
    doc.setLineWidth(1);
    doc.roundedRect(cardX, cardY, contentWidth, cardHeight, 8, 8, 'S');

    doc.setFillColor(245, 247, 255);
    doc.roundedRect(cardX, cardY, contentWidth, headerHeight, 8, 8, 'F');
    doc.setDrawColor(229, 234, 255);
    doc.line(cardX, cardY + headerHeight, cardX + contentWidth, cardY + headerHeight);

    let textY = cardY + 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(51, 84, 253);
    doc.text(`INICIATIVA ${index + 1}`, cardX + cardPadding, textY);
    textY += 18 - 16;

    if (hasTema) {
      const temaLabel = item.tema!.trim().toUpperCase();
      const corHex = getCategoriaCorHex(item.categoryPremio);
      const [r, g, b] = hexToRgb(corHex);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const temaTextWidth = doc.getTextWidth(temaLabel);
      const tagPadX = 8;
      const tagWidth = temaTextWidth + tagPadX * 2;
      const tagX = cardX + cardPadding;
      const tagY = cardY + 22;

      doc.setFillColor(r, g, b);
      doc.roundedRect(tagX, tagY, tagWidth, temaTagHeight, 4, 4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(temaLabel, tagX + tagPadX, tagY + temaTagHeight - 5);

      textY = tagY + temaTagHeight + temaTagGap;
    } else {
      textY = cardY + 24;
    }

    textY += cardPadding;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(17, 18, 20);
    titleLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += 15;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Descrição', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(31, 41, 55);
    descricaoLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += lineHeight;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Resultado', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(31, 41, 55);
    resultadoLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += lineHeight;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Link do vídeo', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(51, 84, 253);
    const videoX = cardX + cardPadding;
    if (hasVideoUrl) {
      doc.textWithLink(videoText, videoX, textY, { url: item.videoUrl as string });
    } else {
      doc.text(videoText, videoX, textY);
    }
    textY += lineHeight;

    doc.setTextColor(0, 0, 0);
    cursorY += cardHeight + 12;
  });

  const filename = `${sanitizeFilename(baseFilename) || 'iniciativas-selecionadas'}.pdf`;
  doc.save(filename);
}
